import { reset as resetPersistence } from '../persistence.js';

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const FELT = '#0d2c1d';
const GOLD = '#d4af37';
const GOLD_LIGHT = '#f4d35e';
const MUTED = '#8a99a3';
const BODY = '#aebfc7';

export interface OpenBustModalOpts {
  parent?: HTMLElement;
  /** Override the reset action — defaults to wiping persistence + full reload,
   *  matching the lobby's PlayerCard "RESET" button. */
  onReset?: () => void;
}

export function openBustModal(opts: OpenBustModalOpts = {}): void {
  const parent = opts.parent ?? document.body;

  const overlay = document.createElement('div');
  overlay.setAttribute('data-casino-bust-modal', '');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:9999',
    'display:flex', 'align-items:center', 'justify-content:center',
    'background:rgba(0,0,0,0.65)',
    `font-family:${FONT}`,
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    `background:${FELT}`, `color:${BODY}`,
    'width:min(92vw,420px)', 'border-radius:12px',
    `border:2px solid ${GOLD}`,
    'padding:20px 22px 18px',
    'box-shadow:0 16px 48px rgba(0,0,0,0.7)',
    'display:flex', 'flex-direction:column', 'gap:14px',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'Tapped out';
  title.style.cssText = [
    'font-size:20px', 'font-weight:bold', `color:${GOLD_LIGHT}`,
    'text-align:center', 'letter-spacing:0.5px',
  ].join(';');

  const subtitle = document.createElement('div');
  subtitle.textContent = 'BANKROLL UNDER $200';
  subtitle.style.cssText = [
    'font-size:11px', 'font-weight:bold', `color:${MUTED}`,
    'text-align:center', 'letter-spacing:1.5px', 'margin-top:-10px',
  ].join(';');

  const message = document.createElement('div');
  message.textContent =
    'Reset to start fresh, or keep playing with what you have.';
  message.style.cssText = [
    'font-size:13px', `color:${BODY}`,
    'text-align:center', 'line-height:1.45',
  ].join(';');

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:4px';
  const keepBtn = makeActionButton('Keep playing', false);
  const resetBtn = makeActionButton('Reset bankroll', true);
  actions.append(keepBtn, resetBtn);

  // Phaser 3 listens at window for pointer/touch events and treats anything
  // whose target isn't the canvas as a game-area pointer — without this
  // swallow, a tap on the modal would also place a bet on whatever zone
  // sits under the tap. Same defense as FeedbackModal.
  const swallow = (e: Event) => e.stopPropagation();
  const SWALLOWED_EVENTS = [
    'pointerdown', 'pointerup', 'pointermove', 'pointercancel',
    'mousedown', 'mouseup', 'mousemove',
    'touchstart', 'touchend', 'touchmove', 'touchcancel',
    'click',
  ] as const;
  for (const type of SWALLOWED_EVENTS) overlay.addEventListener(type, swallow);

  // Keep the overlay in the DOM as an invisible shield for ~320ms after
  // close so the iOS synthesized ghost click can't reach a Phaser bet
  // zone underneath.
  let closing = false;
  const close = () => {
    if (closing) return;
    closing = true;
    overlay.style.background = 'transparent';
    card.style.display = 'none';
    setTimeout(() => overlay.remove(), 320);
  };
  keepBtn.addEventListener('click', close);
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });

  const doReset = opts.onReset ?? (() => {
    resetPersistence();
    location.reload();
  });
  resetBtn.addEventListener('click', () => {
    // No explicit sessionStorage clear here — persistence.reset() owns
    // wiping the bust-modal lock so every reset path (this modal, the
    // lobby's PlayerCard RESET) gets the same behavior for free.
    resetBtn.textContent = 'Resetting…';
    resetBtn.disabled = true;
    keepBtn.disabled = true;
    doReset();
  });

  card.append(title, subtitle, message, actions);
  overlay.append(card);
  parent.append(overlay);
}

function makeActionButton(label: string, primary: boolean): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  b.style.cssText = [
    'padding:10px 18px', 'border-radius:8px', 'cursor:pointer',
    `font:bold 13px ${FONT}`,
    'letter-spacing:0.5px',
    primary
      ? `background:${GOLD};color:${FELT};border:1px solid ${GOLD}`
      : `background:transparent;color:${BODY};border:1px solid ${GOLD}`,
  ].join(';');
  return b;
}
